import React, { useEffect, useState, Suspense, useCallback } from 'react';
import PropTypes from 'prop-types';
import MomentUtils from '@date-io/moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { makeStyles } from '@mui/styles';
import { connect, useSelector } from 'react-redux';
import Amplify, { Auth, Hub } from 'aws-amplify';
import { SnackbarProvider } from 'notistack';
import { green, red, yellow, blue } from '@mui/material/colors';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import DevTools from '../redux/DevTools';
import { authStateSelector, userTypeSelector } from '../redux/selectors';
import Loading from '../components/shared/Loading';
import Login from '../components/Login';
import { validateAuth } from '../util/util';
import { SIGN_IN, SIGN_OUT } from '../util/constants';
import { setAuthState } from '../redux/actions/app';
import Notifier from '../components/shared/Notifier';
import TabLayout from './TabLayout';
import * as Sentry from '@sentry/react';
import FallbackPage from './FallbackPage';
import { useVersionCheck } from '../hooks/useVersionCheck';
import NewVersionBanner, { BANNER_HEIGHT } from '../components/shared/NewVersionBanner';
import MaintenancePage from '../MaintenancePage';

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'absolute',
    bottom: 50,
    left: 50,
  },
  success: {
    backgroundColor: green[500],
    color: '#fff',
  },
  error: {
    backgroundColor: red[500],
    color: '#fff',
  },
  warning: {
    backgroundColor: yellow[500],
    color: '#fff',
  },
  info: {
    backgroundColor: blue[500],
    color: '#fff',
  },
}));

function AppContent(props) {
  const { darkMode, authState, setAuth } = props;
  const [maintenanceConfig, setMaintenanceConfig] = useState({
    activeMaintenance: false,
  });
  const [isActiveMaintenance, setIsActiveMaintenance] = useState(false);
  const classes = useStyles();
  const { newVersionAvailable, serverVersion } = useVersionCheck(authState === SIGN_IN);
  const currentUserType = useSelector(userTypeSelector);
  const setSignIn = useCallback(() => {
    // Block auto sign-in while OTP MFA verification is pending
    if (sessionStorage.getItem('otpMfaPending') === 'true') return;
    if (authState !== SIGN_IN) setAuth(SIGN_IN);
  }, [authState, setAuth]);
  const setSignOut = useCallback(() => {
    if (authState !== SIGN_OUT) setAuth(SIGN_OUT);
  }, [authState, setAuth]);
  const signOut = useCallback(() => {
    Auth.signOut()
      .then()
      .catch((error) => {
        console.error(error);
        setSignOut();
      });
  }, [setSignOut]);

  useEffect(() => {
    Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case SIGN_IN:
          // This will only trigger with normal sign in flow.
          setSignIn();
          break;
        case SIGN_OUT:
          // This will only trigger with normal sign out flow.
          setSignOut();
          break;
        default:
          break;
      }
    });
    // On page load. check if session/tokens is available and valid
    // if valid, set sign in.
    // if invalid, set sign out.
    validateAuth(setSignIn, signOut);
  }, [setSignIn, setSignOut, signOut]);

  useEffect(() => {
    let authExpiryInterval = null;

    if (authState === 'signIn' && !authExpiryInterval) {
      authExpiryInterval = setInterval(
        () => {
          validateAuth(setSignIn, signOut);
        },
        // 1 minute intervals
        60 * 1000
      );
    } else if (authExpiryInterval) {
      clearInterval(authExpiryInterval);
    }
  }, [authState, signOut, setSignIn]);

  // Apply external config if the user type is external
  useEffect(() => {
    if (currentUserType.type === 'external') {
      Amplify.configure({
        API: {
          endpoints: [
            {
              name: process.env.REACT_APP_AWS_API_NAME,
              endpoint: process.env.REACT_APP_AWS_API_EXTERNAL_URL,
            },
          ],
        },
      });
    }
  }, []);

  // Fetch maintenance config from s3, determine if the user needs to be bumped off the site
  useEffect(() => {
    const fetchMaintenanceInformation = async () => {
      try {
        const response = await fetch(
          `https://portal-resources-759125290467-ap-southeast-2-an.s3.ap-southeast-2.amazonaws.com/maintenance.json?t=${Date.now()}`
        );
        const data = await response.json();
        console.log(
          'DCA SITE-MAINT-LOGS',
          data,
          process.env.REACT_APP_ENV,
          data.env[process.env.REACT_APP_ENV]
        );
        const maint = data.env[process.env.REACT_APP_ENV];
        // Only active maintenance for user if the environment has maintenance and targets the user group
        setIsActiveMaintenance(
          maint.active && (maint.target === currentUserType.type || maint.target === 'all')
        );
        setMaintenanceConfig(data);
      } catch (error) {
        console.error('Failed to fetch maintenance information', error);
      }
    };
    fetchMaintenanceInformation();
    const intervalId = setInterval(fetchMaintenanceInformation, 600000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  if (!authState) {
    return <Loading />;
  }

  return (
    <>
      {process.env.NODE_ENV === 'development' && <DevTools />}
      {newVersionAvailable && authState === SIGN_IN && (
        <NewVersionBanner serverVersion={serverVersion} />
      )}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <SnackbarProvider
          classes={{
            variantSuccess: classes.success,
            variantError: classes.error,
            variantWarning: classes.warning,
            variantInfo: classes.info,
          }}
        >
          <Notifier />
          <Suspense fallback={<Loading />}>
            <Sentry.ErrorBoundary fallback={<FallbackPage type="sentry" />}>
              <Router>
                <Switch>
                  <Route path="/site-maintenance">
                    <MaintenancePage
                      title={maintenanceConfig.title}
                      description={maintenanceConfig.description}
                    />
                  </Route>
                  {isActiveMaintenance && <Redirect to="/site-maintenance" />}
                  <Route path="/login">
                    {authState === SIGN_OUT ? <Login darkMode={darkMode} /> : <Redirect to="/" />}
                  </Route>
                  <Route path="/">
                    {authState === SIGN_IN ? (
                      <TabLayout
                        setSignOut={setSignOut}
                        darkMode={darkMode}
                        newVersionAvailable={newVersionAvailable}
                      />
                    ) : (
                      <Redirect to="/login" />
                    )}
                  </Route>
                  <Redirect from="*" to={authState === SIGN_IN ? '/' : '/login'} />
                </Switch>
              </Router>
            </Sentry.ErrorBoundary>
          </Suspense>
        </SnackbarProvider>
      </LocalizationProvider>
    </>
  );
}

AppContent.defaultProps = {
  authState: null,
};

AppContent.propTypes = {
  authState: PropTypes.string,
  setAuth: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({ authState: authStateSelector(state) });

const mapDispatchToProps = (dispatch) => ({ setAuth: (event) => dispatch(setAuthState(event)) });

export default connect(mapStateToProps, mapDispatchToProps)(AppContent);
